import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Sun, Droplets, Sprout, Ruler, Clock, ThumbsUp, ThumbsDown, Lightbulb, ShieldAlert, Loader2, Stethoscope } from "lucide-react";

const SEVERITY = {
  High: "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300",
  Medium: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300",
  Low: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300",
};

export default function Guides() {
  const [vegs, setVegs] = useState(null);
  const [diseases, setDiseases] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    api.get("/guides").then(({ data }) => setVegs(data.vegetables)).catch(() => setVegs([]));
    api.get("/diseases").then(({ data }) => setDiseases(data.diseases)).catch(() => setDiseases([]));
  }, []);

  const openDetail = async (slug) => {
    setLoadingDetail(true);
    setDetail({ loading: true });
    try {
      const { data } = await api.get(`/guides/${slug}`);
      setDetail(data);
    } catch {
      setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Grow Guides</h1>
        <p className="text-muted-foreground mt-1">Sprouting guides, companion plants, and common seedling diseases.</p>
      </div>

      <Tabs defaultValue="vegetables">
        <TabsList className="mb-6" data-testid="guides-tabs">
          <TabsTrigger value="vegetables" data-testid="tab-vegetables">
            <Sprout className="h-4 w-4 mr-1.5" /> Vegetables
          </TabsTrigger>
          <TabsTrigger value="diseases" data-testid="tab-diseases">
            <ShieldAlert className="h-4 w-4 mr-1.5" /> Seedling Diseases
          </TabsTrigger>
        </TabsList>

        <TabsContent value="vegetables">
          {!vegs ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-32 rounded-2xl bg-card border border-border animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="veg-grid">
              {vegs.map((v) => (
                <button
                  key={v.slug}
                  onClick={() => openDetail(v.slug)}
                  className="text-left bg-card rounded-2xl border border-border p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all animate-rise"
                  data-testid={`veg-card-${v.slug}`}
                >
                  <div className="text-3xl mb-2">{v.emoji}</div>
                  <h3 className="font-display font-semibold">{v.name}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{v.category}</p>
                  <div className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" /> {v.days_to_harvest} days
                  </div>
                </button>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="diseases">
          {!diseases ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-16 rounded-xl bg-card border border-border animate-pulse" />
              ))}
            </div>
          ) : (
            <Accordion type="single" collapsible className="space-y-3" data-testid="disease-accordion">
              {diseases.map((d) => (
                <AccordionItem key={d.slug} value={d.slug} className="bg-card rounded-2xl border border-border px-5 shadow-sm">
                  <AccordionTrigger className="hover:no-underline" data-testid={`disease-${d.slug}`}>
                    <div className="flex items-center gap-3 text-left">
                      <Stethoscope className="h-5 w-5 text-emerald-600 shrink-0" />
                      <span className="font-display font-semibold">{d.name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SEVERITY[d.severity] || ""}`}>{d.severity}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid sm:grid-cols-3 gap-5 pt-2 pb-2">
                      <DiseaseCol title="Signs" items={d.signs} tint="text-amber-600" />
                      <DiseaseCol title="Causes" items={d.causes} tint="text-red-600" />
                      <DiseaseCol title="Prevention" items={d.prevention} tint="text-emerald-600" />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={Boolean(detail)} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="guide-detail-dialog">
          <DialogTitle className="sr-only">Grow guide details</DialogTitle>
          {loadingDetail || detail?.loading ? (
            <div className="py-10 flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
            </div>
          ) : detail ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl flex items-center gap-2">
                  <span className="text-3xl">{detail.emoji}</span> {detail.name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-5 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <Stat icon={Clock} label="Germinates" value={`${detail.days_to_germinate} days`} />
                  <Stat icon={Sprout} label="To harvest" value={`${detail.days_to_harvest} days`} />
                  <Stat icon={Sun} label="Sunlight" value={detail.sunlight} />
                  <Stat icon={Ruler} label="Spacing" value={detail.spacing} />
                  <Stat icon={Droplets} label="Water" value={detail.water} />
                  <Stat icon={Ruler} label="Sow depth" value={detail.sow_depth} />
                </div>

                <Section title="Sprouting Guide" icon={Sprout}>
                  <ol className="space-y-2">
                    {detail.sprouting_guide.map((step, i) => (
                      <li key={i} className="flex gap-3 text-sm">
                        <span className="h-5 w-5 rounded-full bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                        {step}
                      </li>
                    ))}
                  </ol>
                </Section>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-semibold flex items-center gap-1.5 text-emerald-600 mb-2"><ThumbsUp className="h-4 w-4" /> Good with</p>
                    <div className="flex flex-wrap gap-1.5">
                      {detail.companions_good.map((c) => (
                        <span key={c} className="px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300">{c}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold flex items-center gap-1.5 text-red-600 mb-2"><ThumbsDown className="h-4 w-4" /> Avoid near</p>
                    <div className="flex flex-wrap gap-1.5">
                      {detail.companions_bad.map((c) => (
                        <span key={c} className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300">{c}</span>
                      ))}
                    </div>
                  </div>
                </div>

                <Section title="Tips" icon={Lightbulb}>
                  <ul className="space-y-1.5">
                    {detail.tips.map((t, i) => (
                      <li key={i} className="flex gap-2 text-sm">
                        <Lightbulb className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" /> {t}
                      </li>
                    ))}
                  </ul>
                </Section>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-secondary/50 rounded-xl p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-0.5">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className="text-sm font-medium">{value}</p>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <h3 className="font-display font-semibold flex items-center gap-2 mb-2.5">
        <Icon className="h-4 w-4 text-emerald-600" /> {title}
      </h3>
      {children}
    </div>
  );
}

function DiseaseCol({ title, items, tint }) {
  return (
    <div>
      <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${tint}`}>{title}</p>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-foreground/90 flex gap-2">
            <span className="text-muted-foreground">•</span> {it}
          </li>
        ))}
      </ul>
    </div>
  );
}
